// The real-time combat system (decisions 0051, 0059): fighters with stats on @rpg/abilities, moves from the game's
// move tables stepped on the fixed sim step, hit volumes swept against hurt sensors through @rpg/collision, the damage
// formula with seeded crits, poise and stagger, health and death, and the defences: dodge with invulnerable steps and
// the perfect dodge, block with stamina drain, chip damage and guard break, the parry, unblockable blows.
//
// The port (seam 2 of phase B): `request(entity, intent)` (taken or not; a player's press that isn't taken waits in
// the game's input buffer), `state(entity)`, `setTarget(entity, target)`. Each sim step the game calls `begin()` (timers
// advance, phases change), then makes its requests (the player's input, the AI), then steps its controllers with
// `drive(entity)` (the lunge, the roll; null: the stick), steps physics, and calls `resolve()` (hit volumes sweep,
// contacts resolve, events go out). All state is the plain `CombatState` the game keeps (saved and hashed with the
// kernel); the package holds definitions and wiring only.
import { createAbilities, type AbilitySet, type AbilityState, type AttributeDef, type EffectDef } from "@rpg/abilities";
import type { BodyId, CollisionWorld } from "@rpg/collision";
import { facing, resolveDefence, toSteps, type DefenceFaults, type DefenceOutcome } from "./defence";
import { damage, poiseAfter } from "./formula";
import { moveSteps, retime, volumeAt, type IntentKind, type MoveDef, type MoveKind, type MovePhase, type TelegraphShape, type Vec3, type WeaponDef } from "./moves";
import { nearestInFront, type ChooseTarget } from "./soft-target";

/** The attributes a fighter's stat set must define (the game's `AttributeDef`s give their bases and clamps). */
export const COMBAT_STATS = ["hp", "hpMax", "attack", "defence", "poise", "poiseMax", "stamina", "staminaMax", "energy", "critChance", "critMult"] as const;

/** A slow-mo request: scale, real seconds, the eased return's seconds. */
export interface SlowMoSpec {
  scale: number;
  seconds: number;
  easeBack: number;
}

/** The game's combat rules (seconds unless named; the game's tuning). */
export interface CombatRules {
  /** The defence curve's constant (damage × K / (K + defence)). */
  defenceK: number;
  /** A block started at most this long before a frontal hit parries it (0.15 s). */
  parryWindow: number;
  /** The parried attacker's stun (0.85 s). */
  parryStun: number;
  /** A block lasts at least this long, however short the press (0.12 s). */
  minBlock: number;
  /** A block covers attackers within this dot product of the facing (0.15: ±81°). */
  blockCos: number;
  /** Fraction of a blocked blow's damage taken anyway (chip). */
  chip: number;
  /** Stamina lost per point of damage blocked. */
  blockDrain: number;
  /** The stun when a block's stamina runs out. */
  guardBreakStun: number;
  /** A blocked blow's hit-stop: the move's × this. */
  blockHitStopScale: number;
  dodge: {
    /** The roll's length, its invulnerable start, its perfect window (all from its start), its speed and stamina cost. */
    seconds: number;
    invuln: number;
    perfect: number;
    speed: number;
    cost: number;
  };
  /** After a perfect dodge: the next attack within `seconds` deals × `mult`. */
  counter: { seconds: number; mult: number };
  /** The stun when poise breaks. */
  stagger: number;
  poiseRegen: { delay: number; rate: number };
  /** Stamina regenerates `rate` per second after `delay` without spending (× `blocking` while blocking). */
  staminaRegen: { delay: number; rate: number; blocking: number };
  hitStop: { parry: number; guardBreak: number; defeat: number };
  slowMo: { perfectDodge: SlowMoSpec; parry: SlowMoSpec; defeat?: SlowMoSpec };
  /** The soft target (until the targeting chooser lands): range and half angle (rad). */
  target: { range: number; halfAngle: number };
}

// ── events (seam 1 of phase B; payloads plain data, vectors as [x, y, z]) ────────────────────────────────────────

/** A hit's kind, for the feedback's recipes. */
export type HitKind = MoveKind | "chip" | "parry" | "guardBreak";

/** One resolved contact. */
export interface HitEvent {
  attacker: number;
  target: number;
  move: string;
  kind: HitKind;
  severity: number;
  damage: number;
  point: Vec3;
  /** Unit, attacker to target. */
  dir: Vec3;
  outcome: DefenceOutcome;
  unblockable: boolean;
  /** A critical hit (not in the seam's list: an addition). */
  crit: boolean;
}

/** A fighter's health reached 0. */
export interface DefeatedEvent {
  entity: number;
  by: number;
  point: Vec3;
  dir: Vec3;
  severity: number;
}

/** A move with a telegraph started its wind-up. */
export interface TelegraphEvent {
  attacker: number;
  move: string;
  shape: TelegraphShape;
  origin: Vec3;
  /** Yaw (rad; 0 faces +Z). */
  facing: number;
  /** Seconds until it is active. */
  lead: number;
  unblockable: boolean;
  /** The game's telegraph style id (colour, pattern, edge; an addition to the seam). */
  style: string;
}

/** A move's (or the dodge's or the block's) phase changed. */
export interface MoveEvent {
  entity: number;
  move: string;
  phase: MovePhase;
  weapon: string | null;
}

/** The events by name. */
export interface CombatEvents {
  "combat:hit": HitEvent;
  "combat:defeated": DefeatedEvent;
  "combat:telegraph": TelegraphEvent;
  "combat:move": MoveEvent;
}

/** What a request asks for. `dir` is the world direction for a dodge (the stick), else ignored. */
export interface Intent {
  kind: IntentKind;
  dir?: Vec3;
}

/** A fighter as the port reports it (seam 2), with a few additions after `alive`. */
export interface FighterView {
  hp: number;
  hpMax: number;
  poise: number;
  stamina: number;
  /** The move running: its id, phase and seconds since it started. */
  move: { id: string; phase: MovePhase; t: number } | null;
  defending: "none" | "block" | "dodge";
  /** Seconds of stun left. */
  stunned: number;
  alive: boolean;
  weapon: string | null;
  energy: number;
  poiseMax: number;
  staminaMax: number;
  /** Seconds left on the weapon's skill cooldown. */
  cooldown: number;
  /** Seconds left on a perfect dodge's counter. */
  counter: number;
  target: number | null;
  /** Its tags now (effects' and loose): statuses and buffs for a HUD. */
  tags: string[];
}

/** The combat port the AI and the game drive fighters through. */
export interface CombatPort {
  request(entity: number, intent: Intent): boolean;
  state(entity: number): FighterView;
  setTarget(entity: number, target: number | null): void;
}

// ── state (plain JSON, kept by the game) ───────────────────────────────────────────────────────────────────────

/** A move running on a fighter. */
export interface ActiveMove {
  id: string;
  /** Steps since it started (0 on its first step). */
  step: number;
  /** Targets it already touched (one contact per move per target). */
  hits: number[];
  landed: number;
  /** The target chosen when it started (its lunge stops short of it), or null. */
  target: number | null;
  /** The facing it holds. */
  yaw: number;
  /** The hit volume's last centre (the sweep starts there). */
  last: Vec3 | null;
  /** Started inside a perfect dodge's counter window. */
  counter: boolean;
  /** Where a target-anchored hit volume is measured from (the target's feet when the move started). */
  anchor?: Vec3;
}

/** One fighter's whole state. */
export interface FighterState {
  id: number;
  team: string;
  body: BodyId;
  hurtLayer: string;
  stats: AbilityState;
  weapon: string | null;
  move: ActiveMove | null;
  defence: { kind: "none" | "block" | "dodge"; age: number; perfectUsed: boolean; dir: [number, number]; release: boolean };
  /** Steps left. */
  stunned: number;
  counter: number;
  /** Steps since its poise was last hit and its stamina last spent. */
  sinceHit: number;
  sinceSpend: number;
  alive: boolean;
  target: number | null;
}

/** Everything combat decides with. Keep it in a kernel component: saves and hashes cover it. */
export interface CombatState {
  fighters: Record<string, FighterState>;
}

/** A new, empty state. */
export const emptyCombatState = (): CombatState => ({ fighters: {} });

/** What a fighter is built from. */
export interface FighterSpec {
  team: string;
  /** Its collision body (it carries the hurt sensor). */
  body: BodyId;
  /** The layer its hurt sensor is on: other teams' hit volumes sweep it. */
  hurtLayer: string;
  weapon?: string | null;
  /** Its stats' starting values (the stat set's bases otherwise). */
  stats?: Record<string, number>;
}

/** Planted defects beside what they break (tests only). */
export const COMBAT_FAULTS: DefenceFaults & {
  /** Every move's active window runs one step long: the frame-data check must go red. */
  activeExtraStep: boolean;
} = { parryWindowShift: false, activeExtraStep: false };

/** What `createCombat` needs from the game. */
export interface CombatOptions {
  /** The sim's step rate. */
  stepHz: number;
  /** The move tables' step rate (default `stepHz`); moves are retimed when they differ. */
  movesHz?: number;
  rules: CombatRules;
  moves: readonly MoveDef[];
  weapons: readonly WeaponDef[];
  /** The stat set (must define `COMBAT_STATS`), and effects the game applies by id. */
  stats: readonly AttributeDef[];
  effects?: readonly EffectDef[];
  /** The collision world the hurt sensors live in. */
  world: CollisionWorld;
  /** Where a fighter stands (its feet) and which way it faces, this step. */
  pose(entity: number): { feet: Vec3; yaw: number };
  /** The state the game keeps. */
  state: CombatState;
  emit<K extends keyof CombatEvents>(name: K, payload: CombatEvents[K]): void;
  /** The runtime clock's hit-stop and slow-mo. Leave `hitStop` out when the game's impact director owns hit-stop
   *  (one owner: it sets the hold by the hit's kind from `combat:hit` and `combat:defeated`); combat then requests
   *  none, and the moves' and rules' hit-stop numbers go unused. Slow-mo (the perfect dodge, the parry) stays combat's. */
  clock: { hitStop?(seconds: number): void; slowMo(scale: number, seconds: number, easeBack: number): void };
  /** A number in [0, 1) from the game's seeded stream (crits). */
  random(): number;
  /** The soft-target chooser (default: the nearest live fighter of another team in front, inside `rules.target`). */
  chooseTarget?: ChooseTarget;
  /** A target's resistance (0..1) to a blow's damage tags (default 0). */
  resist?(target: number, tags: readonly string[]): number;
  /** Any further damage multiplier for a blow (difficulty, assists; default 1). */
  damageScale?(attacker: number, target: number): number;
  /** Called for every step a hit volume is live and swept, with its centre (frame-data checks count these; a debug
   *  view may draw them). */
  onSweep?(entity: number, move: string, step: number, at: Vec3, radius: number): void;
  /** The move a fighter actually starts for one it asks for (a skill tree's upgrade swaps it; default: the same). */
  moveFor?(entity: number, move: string): string;
  /** The tag that holds a fighter stunned, like a parry's or a stagger's stun, while an effect grants it (a stun
   *  effect, decision 0050). Default `state.stunned`. */
  stunTag?: string;
}

/** The combat system. */
export interface Combat extends CombatPort {
  readonly abilities: AbilitySet;
  /** The moves by id, retimed to the sim's rate. */
  readonly moves: ReadonlyMap<string, MoveDef>;
  readonly weapons: ReadonlyMap<string, WeaponDef>;
  /** Defence windows in steps (from the rules at the sim's rate). */
  readonly windows: { parry: number; invuln: number; perfect: number; dodge: number; minBlock: number };
  /** Add a fighter for a kernel entity. Throws if it exists or its weapon is unknown. */
  addFighter(entity: number, spec: FighterSpec): FighterState;
  removeFighter(entity: number): void;
  has(entity: number): boolean;
  /** Every fighter's id, ascending. */
  fighters(): number[];
  /** Change weapon (only between moves). */
  setWeapon(entity: number, weapon: string | null): boolean;
  /** Back to full health, poise and stamina, alive, idle (a dummy's or a respawn's reset). */
  revive(entity: number): void;
  /** Step part 1, at the start of the sim step: timers, phases, regeneration. */
  begin(): void;
  /** The move override for a fighter's controller this step (a lunge, a roll, rooted), or null for the stick. */
  drive(entity: number): { x: number; z: number; yaw?: number } | null;
  /** Step part 2, after physics: sweep the hit volumes, resolve contacts, emit events. */
  resolve(): void;
  /** The phase a move is in at a step, as this system counts it (planted defects included). */
  phaseOf(move: MoveDef, step: number): MovePhase;
}

const round3 = (v: number): number => Math.round(v * 1000) / 1000;

/** Build the combat system over the game's tables and wiring. Throws on a stat set missing a combat stat. */
export function createCombat(o: CombatOptions): Combat {
  const hz = o.stepHz;
  const dt = 1 / hz;
  for (const id of COMBAT_STATS) if (!o.stats.some((a) => a.id === id)) throw new Error(`combat: the stat set has no "${id}"`);
  const moves = new Map<string, MoveDef>(o.moves.map((m) => [m.id, retime(m, o.movesHz ?? hz, hz)]));
  const weapons = new Map<string, WeaponDef>(o.weapons.map((w) => [w.id, w]));
  const skillId = (w: string): string => `skill:${w}`;
  const bigId = (w: string): string => `big:${w}`;
  const abilities = createAbilities({
    attributes: o.stats,
    effects: o.effects ?? [],
    abilities: o.weapons.flatMap((w) => [
      ...(w.skill ? [{ id: skillId(w.id), tags: ["ability.skill"], ...(w.cooldown ? { cooldown: w.cooldown } : {}) }] : []),
      ...(w.big ? [{ id: bigId(w.id), tags: ["ability.big"], cost: [{ attribute: "energy", amount: w.bigCost ?? 100 }] }] : []),
    ]),
  });
  const r = o.rules;
  const windows = {
    parry: toSteps(r.parryWindow, hz),
    invuln: toSteps(r.dodge.invuln, hz),
    perfect: toSteps(r.dodge.perfect, hz),
    dodge: toSteps(r.dodge.seconds, hz),
    minBlock: toSteps(r.minBlock, hz),
  };
  const choose = o.chooseTarget ?? nearestInFront(r.target);
  const S = (): CombatState => o.state;
  const get = (e: number): FighterState => {
    const f = S().fighters[e];
    if (!f) throw new Error(`combat: no fighter ${e}`);
    return f;
  };
  const ids = (): number[] =>
    Object.keys(S().fighters)
      .map(Number)
      .sort((a, b) => a - b);
  const stat = (f: FighterState, id: string): number => abilities.value(f.stats, id);
  // a stun effect holds a fighter like a parry's stun (decision 0050's statuses); a skill tree may swap a move
  const stunTag = o.stunTag ?? "state.stunned";
  const tagStun = (f: FighterState): number => {
    const t = abilities.tagTime(f.stats, stunTag);
    return Number.isFinite(t) ? t : 3600;
  };
  const stunnedNow = (f: FighterState): boolean => f.stunned > 0 || tagStun(f) > 0;
  const moveOf = (f: FighterState, id: string): MoveDef | undefined => {
    const swapped = o.moveFor?.(f.id, id);
    return (swapped !== undefined ? moves.get(swapped) : undefined) ?? moves.get(id);
  };
  const setStat = (f: FighterState, id: string, v: number): void => abilities.setBase(f.stats, id, v);

  const phaseOf = (m: MoveDef, step: number): MovePhase => {
    // the planted defect sits here, beside the window it lengthens
    const active = m.active + (COMBAT_FAULTS.activeExtraStep ? 1 : 0);
    if (step < m.windup) return "windup";
    if (step < m.windup + active) return "active";
    if (step < m.windup + active + m.recovery) return "recovery";
    return "end";
  };

  const emitMove = (f: FighterState, move: string, phase: MovePhase): void => o.emit("combat:move", { entity: f.id, move, phase, weapon: f.weapon });

  const endMove = (f: FighterState): void => {
    if (!f.move) return;
    const id = f.move.id;
    f.move = null;
    emitMove(f, id, "end");
  };
  const endDefence = (f: FighterState): void => {
    if (f.defence.kind === "none") return;
    const k = f.defence.kind;
    f.defence = { kind: "none", age: 0, perfectUsed: false, dir: [0, 0], release: false };
    emitMove(f, k, "end");
  };

  const bearing = (from: Vec3, to: Vec3): number => Math.atan2(to[0] - from[0], to[2] - from[2]);

  const startMove = (f: FighterState, def: MoveDef): void => {
    endMove(f); // a chain or a cancel ends the move it leaves (its "end" event goes out first)
    const me = o.pose(f.id);
    const others = ids()
      .filter((id) => id !== f.id && get(id).team !== f.team)
      .map((id) => ({ id, pos: o.pose(id).feet, alive: get(id).alive }));
    const locked = f.target !== null && S().fighters[f.target]?.alive ? f.target : null;
    const target = locked ?? choose({ pos: me.feet, facing: me.yaw }, others);
    const yaw = target !== null ? bearing(me.feet, o.pose(target).feet) : me.yaw;
    const anchor: Vec3 | undefined = def.hit.anchor === "target" && target !== null ? [...o.pose(target).feet] : undefined;
    f.move = { id: def.id, step: 0, hits: [], landed: 0, target, yaw, last: null, counter: f.counter > 0, ...(anchor ? { anchor } : {}) };
    f.counter = 0;
    emitMove(f, def.id, phaseOf(def, 0));
    if (def.telegraph) {
      const t = def.telegraph;
      const off = t.offset ?? 0;
      const base = anchor ?? me.feet;
      o.emit("combat:telegraph", {
        attacker: f.id,
        move: def.id,
        shape: t.shape,
        origin: [base[0] + Math.sin(yaw) * off, base[1], base[2] + Math.cos(yaw) * off],
        facing: yaw,
        lead: def.windup * dt,
        unblockable: def.unblockable === true,
        style: t.style,
      });
    }
  };

  /** Whether the running move lets `kind` cancel it now. */
  const cancellable = (f: FighterState, kind: IntentKind): boolean => {
    const m = f.move;
    if (!m) return true;
    const def = moves.get(m.id)!;
    return (def.cancels ?? []).some((c) => c.into.includes(kind) && m.step >= c.from && m.step < (c.to ?? moveSteps(def)) && (!c.onHit || m.landed > 0));
  };

  const request = (entity: number, intent: Intent): boolean => {
    const f = S().fighters[entity];
    if (!f || !f.alive) return false;
    if (intent.kind === "blockRelease") {
      if (f.defence.kind === "block") f.defence.release = true;
      return true;
    }
    if (stunnedNow(f)) return false;
    const weapon = f.weapon ? weapons.get(f.weapon) : undefined;
    switch (intent.kind) {
      case "block": {
        if (f.defence.kind === "block") {
          f.defence.release = false;
          return true;
        }
        if (f.defence.kind === "dodge") return false;
        if (!cancellable(f, "block")) return false;
        endMove(f);
        f.defence = { kind: "block", age: 0, perfectUsed: false, dir: [0, 0], release: false };
        emitMove(f, "block", "active");
        return true;
      }
      case "dodge": {
        if (f.defence.kind === "dodge") return false;
        if (stat(f, "stamina") < r.dodge.cost - 1e-9) return false;
        if (!cancellable(f, "dodge")) return false;
        const me = o.pose(f.id);
        let dx = intent.dir?.[0] ?? 0;
        let dz = intent.dir?.[2] ?? 0;
        const m = Math.hypot(dx, dz);
        if (m < 1e-6) {
          dx = Math.sin(me.yaw);
          dz = Math.cos(me.yaw);
        } else {
          dx /= m;
          dz /= m;
        }
        endMove(f);
        endDefence(f);
        setStat(f, "stamina", abilities.base(f.stats, "stamina") - r.dodge.cost);
        f.sinceSpend = 0;
        f.defence = { kind: "dodge", age: 0, perfectUsed: false, dir: [dx, dz], release: false };
        emitMove(f, "dodge", "active");
        return true;
      }
      case "attack": {
        if (!weapon || f.defence.kind !== "none") return false;
        const m = f.move;
        if (m) {
          const def = moves.get(m.id)!;
          if (def.next && def.chainFrom !== undefined && m.step >= def.chainFrom) {
            startMove(f, moveOf(f, def.next)!);
            return true;
          }
          if (!cancellable(f, "attack")) return false;
        }
        const first = moveOf(f, weapon.combo[0]!);
        if (!first) return false;
        startMove(f, first);
        return true;
      }
      case "skill":
      case "big": {
        const id = intent.kind === "skill" ? weapon?.skill : weapon?.big;
        if (!weapon || !id || f.defence.kind !== "none") return false;
        if (f.move && !cancellable(f, intent.kind)) return false;
        const ab = intent.kind === "skill" ? skillId(weapon.id) : bigId(weapon.id);
        if (abilities.activate(f.stats, ab) !== null) return false;
        startMove(f, moveOf(f, id)!);
        return true;
      }
    }
    return false;
  };

  const view = (entity: number): FighterView => {
    const f = get(entity);
    const def = f.move ? moves.get(f.move.id) : undefined;
    return {
      hp: stat(f, "hp"),
      hpMax: stat(f, "hpMax"),
      poise: round3(stat(f, "poise")),
      stamina: round3(stat(f, "stamina")),
      move: f.move && def ? { id: f.move.id, phase: phaseOf(def, f.move.step), t: f.move.step * dt } : null,
      defending: f.defence.kind,
      stunned: Math.max(f.stunned * dt, tagStun(f)),
      alive: f.alive,
      weapon: f.weapon,
      energy: stat(f, "energy"),
      poiseMax: stat(f, "poiseMax"),
      staminaMax: stat(f, "staminaMax"),
      cooldown: f.weapon ? abilities.cooldownLeft(f.stats, skillId(f.weapon)) : 0,
      counter: f.counter * dt,
      target: f.target,
      tags: abilities.tags(f.stats),
    };
  };

  const begin = (): void => {
    for (const id of ids()) {
      const f = S().fighters[id];
      if (!f || !f.alive) continue;
      // who is hurting it over time (a burn's source takes the defeat if a tick ends it)
      const harm = f.stats.effects.filter((e) => e.nextTick !== null && e.source !== null && e.source !== f.id).map((e) => e.source!);
      abilities.step(f.stats, dt);
      if (stat(f, "hp") <= 0) {
        const me = o.pose(f.id);
        defeat(f, harm[0] ?? f.id, [me.feet[0], me.feet[1] + 1, me.feet[2]], [-Math.sin(me.yaw), 0, -Math.cos(me.yaw)], 0.5);
        continue;
      }
      // a stun effect cuts off what it is doing, as a stagger does
      if (tagStun(f) > 0 && (f.move || f.defence.kind !== "none")) {
        endMove(f);
        endDefence(f);
      }
      if (f.stunned > 0) f.stunned--;
      if (f.counter > 0) f.counter--;
      f.sinceHit++;
      f.sinceSpend++;
      if (f.sinceHit * dt >= r.poiseRegen.delay) setStat(f, "poise", abilities.base(f.stats, "poise") + r.poiseRegen.rate * dt);
      if (f.sinceSpend * dt >= r.staminaRegen.delay) {
        const rate = r.staminaRegen.rate * (f.defence.kind === "block" ? r.staminaRegen.blocking : 1);
        setStat(f, "stamina", abilities.base(f.stats, "stamina") + rate * dt);
      }
      if (f.defence.kind !== "none") {
        f.defence.age++;
        if (f.defence.kind === "dodge" && f.defence.age >= windows.dodge) endDefence(f);
        else if (f.defence.kind === "block" && f.defence.release && f.defence.age >= windows.minBlock) endDefence(f);
      }
      if (f.move) {
        const def = moves.get(f.move.id)!;
        const before = phaseOf(def, f.move.step);
        f.move.step++;
        const now = phaseOf(def, f.move.step);
        if (now === "end") endMove(f);
        else if (now !== before) emitMove(f, def.id, now);
      }
    }
  };

  const drive = (entity: number): { x: number; z: number; yaw?: number } | null => {
    const f = S().fighters[entity];
    if (!f || !f.alive) return null;
    if (stunnedNow(f)) return { x: 0, z: 0 };
    if (f.defence.kind === "dodge") {
      const k = Math.min(1, f.defence.age / Math.max(1, windows.dodge));
      const v = r.dodge.speed * (1 - 0.5 * k * k);
      return { x: f.defence.dir[0] * v, z: f.defence.dir[1] * v, yaw: Math.atan2(f.defence.dir[0], f.defence.dir[1]) };
    }
    if (f.defence.kind === "block") {
      const t = f.target !== null && S().fighters[f.target]?.alive ? f.target : null;
      return t !== null ? { x: 0, z: 0, yaw: bearing(o.pose(entity).feet, o.pose(t).feet) } : { x: 0, z: 0 };
    }
    const m = f.move;
    if (!m) return null;
    const def = moves.get(m.id)!;
    const l = def.lunge;
    if (l && m.step >= l.from && m.step < l.to) {
      const me = o.pose(entity).feet;
      // the lunge stops `stopAt` short of its target: at most the room left this step, none once there
      let speed = l.speed;
      if (m.target !== null && S().fighters[m.target]) {
        const t = o.pose(m.target).feet;
        const room = Math.hypot(t[0] - me[0], t[2] - me[2]) - l.stopAt;
        speed = Math.max(0, Math.min(speed, room / dt));
      }
      return { x: Math.sin(m.yaw) * speed, z: Math.cos(m.yaw) * speed, yaw: m.yaw };
    }
    return { x: 0, z: 0, yaw: m.yaw };
  };

  const defeat = (t: FighterState, by: number, point: Vec3, dir: Vec3, severity: number): void => {
    t.alive = false;
    setStat(t, "hp", 0);
    endMove(t);
    endDefence(t);
    t.stunned = 0;
    o.emit("combat:defeated", { entity: t.id, by, point, dir, severity });
    o.clock.hitStop?.(r.hitStop.defeat);
    if (r.slowMo.defeat) o.clock.slowMo(r.slowMo.defeat.scale, r.slowMo.defeat.seconds, r.slowMo.defeat.easeBack);
  };

  const contact = (a: FighterState, t: FighterState, def: MoveDef, point: Vec3): void => {
    const m = a.move!;
    m.hits.push(t.id);
    const pa = o.pose(a.id).feet;
    const pt = o.pose(t.id).feet;
    let dx = pt[0] - pa[0];
    let dz = pt[2] - pa[2];
    const len = Math.hypot(dx, dz);
    if (len > 1e-9) {
      dx /= len;
      dz /= len;
    } else {
      dx = Math.sin(m.yaw);
      dz = Math.cos(m.yaw);
    }
    const dir: Vec3 = [dx, 0, dz];
    const unblockable = def.unblockable === true;
    const outcome = resolveDefence(
      { defending: t.defence.kind, age: t.defence.age, perfectUsed: t.defence.perfectUsed, yaw: o.pose(t.id).yaw },
      { x: -dx, z: -dz },
      unblockable,
      { parrySteps: windows.parry, invulnSteps: windows.invuln, perfectSteps: windows.perfect, blockCos: r.blockCos },
      COMBAT_FAULTS,
    );
    const roll = o.random();
    const dmg = damage({
      attack: stat(a, "attack"),
      power: def.power,
      defence: stat(t, "defence"),
      defenceK: r.defenceK,
      critChance: stat(a, "critChance"),
      critMult: stat(a, "critMult"),
      roll,
      resist: o.resist?.(t.id, def.damage ?? []) ?? 0,
      scale: (o.damageScale?.(a.id, t.id) ?? 1) * (m.counter ? r.counter.mult : 1),
    });
    let kind: HitKind = def.kind;
    let amount = 0;
    let severity = def.severity;
    if (outcome === "dodged") {
      // nothing more: the dodge's invulnerable steps took it
    } else if (outcome === "perfectDodge") {
      t.defence.perfectUsed = true;
      t.counter = toSteps(r.counter.seconds, hz);
      o.clock.slowMo(r.slowMo.perfectDodge.scale, r.slowMo.perfectDodge.seconds, r.slowMo.perfectDodge.easeBack);
    } else if (outcome === "parried") {
      kind = "parry";
      severity = Math.max(severity, 0.8);
      endMove(a);
      a.stunned = toSteps(r.parryStun, hz);
      o.clock.hitStop?.(r.hitStop.parry);
      o.clock.slowMo(r.slowMo.parry.scale, r.slowMo.parry.seconds, r.slowMo.parry.easeBack);
    } else if (outcome === "blocked") {
      amount = dmg.amount > 0 ? Math.max(1, Math.round(dmg.amount * r.chip)) : 0;
      setStat(t, "stamina", abilities.base(t.stats, "stamina") - dmg.amount * r.blockDrain);
      t.sinceSpend = 0;
      setStat(t, "hp", abilities.base(t.stats, "hp") - amount);
      if (stat(t, "stamina") <= 1e-9) {
        kind = "guardBreak";
        severity = Math.max(severity, 0.9);
        endDefence(t);
        t.stunned = toSteps(r.guardBreakStun, hz);
        o.clock.hitStop?.(r.hitStop.guardBreak);
      } else {
        kind = "chip";
        o.clock.hitStop?.(def.hitStop * r.blockHitStopScale);
      }
      setStat(a, "energy", abilities.base(a.stats, "energy") + (def.charge ?? 0));
      m.landed++;
    } else {
      amount = dmg.amount;
      setStat(t, "hp", abilities.base(t.stats, "hp") - amount);
      const tm = t.move;
      const td = tm ? moves.get(tm.id) : undefined;
      const armoured = !!(tm && td?.armour && tm.step >= td.armour[0] && tm.step < td.armour[1]);
      const p = poiseAfter(abilities.base(t.stats, "poise"), def.poise, stat(t, "poiseMax"), armoured);
      setStat(t, "poise", p.poise);
      t.sinceHit = 0;
      if (p.broken) {
        endMove(t);
        endDefence(t);
        t.stunned = toSteps(r.stagger, hz);
      }
      setStat(a, "energy", abilities.base(a.stats, "energy") + (def.charge ?? 0));
      m.landed++;
      o.clock.hitStop?.(def.hitStop);
      // the move's statuses (a burn, a slow, a stun), each on its chance; a stun cuts the target's move off
      for (const ap of def.applies ?? []) {
        if (ap.chance !== undefined && ap.chance < 1 && o.random() >= ap.chance) continue;
        abilities.apply(t.stats, ap.effect, { source: a.id });
      }
      if (def.applies?.length && tagStun(t) > 0) {
        endMove(t);
        endDefence(t);
      }
    }
    o.emit("combat:hit", { attacker: a.id, target: t.id, move: def.id, kind, severity, damage: amount, point, dir, outcome, unblockable, crit: amount > 0 && outcome === "hit" && dmg.crit });
    if (t.alive && stat(t, "hp") <= 0) defeat(t, a.id, point, dir, severity);
  };

  const resolve = (): void => {
    const all = ids();
    const byBody = new Map<string, number>(all.map((id) => [get(id).body, id]));
    for (const id of all) {
      const a = get(id);
      if (!a.alive || !a.move) continue;
      const def = moves.get(a.move.id)!;
      if (phaseOf(def, a.move.step) !== "active") continue;
      const layers = [...new Set(all.filter((x) => get(x).team !== a.team).map((x) => get(x).hurtLayer))];
      if (!layers.length) continue;
      const me = o.pose(id);
      const i = a.move.step - def.windup;
      const at = volumeAt(def, Math.min(i, def.active - 1), a.move.anchor ?? me.feet, a.move.yaw);
      const from = a.move.last ?? at;
      a.move.last = at;
      o.onSweep?.(id, def.id, a.move.step, at, def.hit.radius);
      const hits = o.world.sweep({ type: "ball", radius: def.hit.radius }, { x: from[0], y: from[1], z: from[2] }, { x: at[0], y: at[1], z: at[2] }, { x: 0, y: 0, z: 0, w: 1 }, { layers, exclude: a.body });
      for (const h of hits) {
        const tid = byBody.get(h.id);
        if (tid === undefined || tid === id) continue;
        const t = get(tid);
        if (!t.alive || t.team === a.team || a.move.hits.includes(tid)) continue;
        contact(a, t, def, [h.at.x, h.at.y, h.at.z]);
        if (!a.move) break; // parried: the move ended
      }
    }
  };

  const combat: Combat = {
    abilities,
    moves,
    weapons,
    windows,
    phaseOf,
    addFighter(entity, spec) {
      if (S().fighters[entity]) throw new Error(`combat: fighter ${entity} exists`);
      if (spec.weapon && !weapons.has(spec.weapon)) throw new Error(`combat: unknown weapon "${spec.weapon}"`);
      const f: FighterState = {
        id: entity,
        team: spec.team,
        body: spec.body,
        hurtLayer: spec.hurtLayer,
        stats: abilities.init(spec.stats),
        weapon: spec.weapon ?? null,
        move: null,
        defence: { kind: "none", age: 0, perfectUsed: false, dir: [0, 0], release: false },
        stunned: 0,
        counter: 0,
        sinceHit: 0,
        sinceSpend: 0,
        alive: true,
        target: null,
      };
      S().fighters[entity] = f;
      return f;
    },
    removeFighter(entity) {
      delete S().fighters[entity];
    },
    has: (e) => S().fighters[e] !== undefined,
    fighters: ids,
    setWeapon(entity, weapon) {
      const f = get(entity);
      if (f.move || (weapon !== null && !weapons.has(weapon))) return false;
      f.weapon = weapon;
      return true;
    },
    revive(entity) {
      const f = get(entity);
      for (const [stat, max] of [["hp", "hpMax"], ["poise", "poiseMax"], ["stamina", "staminaMax"]] as const) setStat(f, stat, abilities.value(f.stats, max));
      f.alive = true;
      f.move = null;
      f.defence = { kind: "none", age: 0, perfectUsed: false, dir: [0, 0], release: false };
      f.stunned = 0;
      f.counter = 0;
    },
    request,
    state: view,
    setTarget(entity, target) {
      get(entity).target = target;
    },
    begin,
    drive,
    resolve,
  };
  return combat;
}

/** A defence's facing test, exported for the game's views (true when `dir` is within `cos` of the facing). */
export const facesToward = (yaw: number, dir: { x: number; z: number }, cos: number): boolean => facing(yaw, dir) > cos;
